/**
 * useAfkDetect — detect user inactivity and fire status changes:
 *   active   → Online (0) within 1s after any input
 *   idle 5m  → AFK    (1)
 *   idle 30m → Asleep (7)
 *
 * "Activity" = mousemove / keypress / focus / scroll. The hook stores
 * the last-activity timestamp in a ref; a setInterval ticks every 30s
 * to evaluate transitions. Setters are no-ops if the new status matches
 * the current one, so this is cheap.
 */
import { useEffect, useRef } from 'react';

const AFK_MS    = 5 * 60_000;     // 5 minutes
const ASLEEP_MS = 30 * 60_000;    // 30 minutes
const TICK_MS   = 30_000;

interface Opts {
    /** Current `me.uStatus` (0–9). */
    currentStatus: number;
    /** Called when the hook wants to change the user's status. */
    onChangeStatus: (s: number) => void;
    /** Disable when user explicitly set a non-auto status (e.g. Busy). */
    enabled?: boolean;
}

export function useAfkDetect({ currentStatus, onChangeStatus, enabled = true }: Opts): void {
    const lastActiveRef = useRef<number>(Date.now());
    const lastSentRef   = useRef<number>(currentStatus);
    lastSentRef.current = currentStatus;

    useEffect(() => {
        if (!enabled) return;
        function bump() { lastActiveRef.current = Date.now(); }
        window.addEventListener('mousemove', bump, { passive: true });
        window.addEventListener('keydown',  bump);
        window.addEventListener('focus',    bump);
        window.addEventListener('scroll',   bump, { passive: true });
        return () => {
            window.removeEventListener('mousemove', bump);
            window.removeEventListener('keydown',   bump);
            window.removeEventListener('focus',     bump);
            window.removeEventListener('scroll',    bump);
        };
    }, [enabled]);

    useEffect(() => {
        if (!enabled) return;
        const tick = () => {
            const idle = Date.now() - lastActiveRef.current;
            let want: number;
            if      (idle >= ASLEEP_MS) want = 7;
            else if (idle >= AFK_MS)    want = 1;
            else                        want = 0;
            // Only auto-transition between {0, 1, 7}. If the user explicitly
            // chose Busy (2) or In MP (3) etc., respect that.
            const cur = lastSentRef.current;
            const autoSet = cur === 0 || cur === 1 || cur === 7;
            if (autoSet && want !== cur) {
                lastSentRef.current = want;
                onChangeStatus(want);
            }
        };
        const id = setInterval(tick, TICK_MS);
        return () => clearInterval(id);
    }, [enabled, onChangeStatus]);
}
